import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Button,
    Text,
    VStack,
    HStack,
    Badge,
    Divider,
    Box,
    Flex,
    Link,
    Icon,
    useToast,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    InputGroup,
    InputRightElement,
    Input,
    IconButton,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    Tag,
    TagLabel,
    useDisclosure,
    Drawer,
    DrawerOverlay,
    DrawerContent,
} from '@chakra-ui/react';
import {
    MdOpenInNew,
    MdDateRange,
    MdPlace,
    MdPictureAsPdf,
    MdRefresh,
    MdGavel,
    MdAssignment,
    MdSummarize,
    MdChat,
    MdInfo,
    MdMoreVert,
    MdAdd,
    MdDelete,
    MdEdit,
    MdStars
} from 'react-icons/md';
import { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import apiClient, { aiApi } from '@/services/api';
import {
    useSummarizeProposal,
    useChatHistory,
    useChatSessions,
    useCreateChatSession,
    useRenameChatSession,
    useDeleteChatSession
} from '@/services/hooks';
import { PDFPreviewModal } from '@/components/ImportedItems/PDFPreviewModal';
import { ConfirmDialog } from '@/components/Common/ConfirmDialog';
import { RenameDialog } from './RenameDialog';

interface ItemDetailsModalProps {
    isOpen: boolean;
    onClose: () => void;
    item: any | null;
    onMove: (item: any) => void;
    isMoving: boolean;
    onRefresh?: () => void;
}

export function ItemDetailsModal({ isOpen, onClose, item, onMove, isMoving, onRefresh }: ItemDetailsModalProps) {
    const [isFetching, setIsFetching] = useState(false);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [previewPage, setPreviewPage] = useState<number | undefined>(undefined);
    const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
    const [localSummary, setLocalSummary] = useState<string | null>(null);
    const [localConfidence, setLocalConfidence] = useState<number | null>(null);
    const [chatInput, setChatInput] = useState('');
    const [messages, setMessages] = useState<{ role: 'user' | 'ai'; content: string }[]>([]);
    const [currentSessionId, setCurrentSessionId] = useState<number | null>(null);
    const [isStreaming, setIsStreaming] = useState(false);

    const chatScrollRef = useRef<HTMLDivElement>(null);
    const toast = useToast();

    // AI Hooks
    const summaryMutation = useSummarizeProposal();

    const { data: sessions = [] } = useChatSessions({
        imported_item_id: item?.id
    });

    const { data: historyData } = useChatHistory({
        session_id: currentSessionId || undefined,
        imported_item_id: !currentSessionId ? item?.id : undefined
    });

    const createSessionMutation = useCreateChatSession();
    const renameSessionMutation = useRenameChatSession();
    const deleteSessionMutation = useDeleteChatSession();

    // Confirmation Dialog State
    const [confirmConfig, setConfirmConfig] = useState<{
        title: string;
        message: string;
        confirmText: string;
        confirmColor: string;
        onConfirm: () => void;
        isLoading: boolean;
    } | null>(null);

    const { isOpen: isDetailsConfirmOpen, onOpen: onDetailsConfirmOpen, onClose: onDetailsConfirmClose } = useDisclosure();

    // Rename Dialog State
    const { isOpen: isRenameOpen, onOpen: onRenameOpen, onClose: onRenameClose } = useDisclosure();
    const [sessionToRename, setSessionToRename] = useState<{ id: number; title: string } | null>(null);
    // Oops, I can use separate disclosure for the confirm dialog since it's a separate component.
    // Actually, I'll just use one set for the modal's confirm needs.

    // Auto-scroll chat
    useEffect(() => {
        if (chatScrollRef.current) {
            chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
        }
    }, [messages]);

    const { isOpen: isSessionsOpen, onOpen: onSessionsOpen, onClose: onSessionsClose } = useDisclosure();

    const SessionList = () => (
        <VStack spacing={2} align="stretch" py={2}>
            <Button size="sm" leftIcon={<MdAdd />} colorScheme="brand" variant="ghost" onClick={() => { handleCreateSession(); onSessionsClose(); }} borderRadius="lg" mb={2} justifyContent="flex-start">New Thread</Button>
            {sessions.map((session: any) => (
                <HStack
                    key={session.id}
                    p={2}
                    borderRadius="md"
                    cursor="pointer"
                    bg={currentSessionId === session.id ? 'brand.100' : 'transparent'}
                    _dark={{ bg: currentSessionId === session.id ? 'brand.900' : 'transparent' }}
                    _hover={{ bg: currentSessionId === session.id ? 'brand.200' : 'gray.100', _dark: { bg: 'whiteAlpha.100' } }}
                    onClick={() => { setCurrentSessionId(session.id); onSessionsClose(); }}
                    role="group"
                >
                    <Icon as={MdChat} color={currentSessionId === session.id ? 'brand.500' : 'gray.400'} />
                    <Text fontSize="xs" fontWeight={currentSessionId === session.id ? 'bold' : 'normal'} noOfLines={1} flex={1}>{session.title}</Text>
                    <Menu isLazy>
                        <MenuButton as={IconButton} size="xs" icon={<MdMoreVert />} variant="ghost" opacity={0} _groupHover={{ opacity: 1 }} />
                        <MenuList zIndex={2000}>
                            <MenuItem icon={<MdEdit />} fontSize="xs" onClick={(e) => { e.stopPropagation(); handleRenameSession(session.id, session.title); }}>Rename</MenuItem>
                            <MenuItem icon={<MdDelete />} fontSize="xs" color="red.500" onClick={(e) => { e.stopPropagation(); handleDeleteSession(session.id); }}>Delete</MenuItem>
                        </MenuList>
                    </Menu>
                </HStack>
            ))}
        </VStack>
    );

    // Isolation: Reset modal state when item changes
    useEffect(() => {
        if (item?.id) {
            setMessages([]);
            setChatInput('');
            setLocalSummary(null);
            setLocalConfidence(null);
            setCurrentSessionId(null);
        }
    }, [item?.id]);

    // Select first session if available
    useEffect(() => {
        if (sessions.length > 0 && !currentSessionId) {
            setCurrentSessionId(sessions[0].id);
        }
    }, [sessions, currentSessionId]);

    // Load history into messages
    useEffect(() => {
        if (historyData?.messages) {
            setMessages(historyData.messages.map((m: any) => ({
                role: m.role,
                content: m.content
            })));
        } else if (!currentSessionId) {
            setMessages([]);
        }
    }, [historyData, currentSessionId]);

    if (!item) return null;

    const displaySummary = localSummary || item.ai_notes;
    const displayConfidence = localConfidence || item.confidence_score;

    const handleFetchPdf = async () => {
        setIsFetching(true);
        try {
            await apiClient.post(`/imported/${item.id}/fetch-document`);
            toast({
                title: 'PDF fetch started',
                description: 'The document will be available shortly. Refresh to check.',
                status: 'info',
                duration: 4000,
            });
            setTimeout(() => onRefresh?.(), 3000);
        } catch (err) {
            toast({ title: 'Failed to start PDF fetch', status: 'error', duration: 3000 });
        } finally {
            setIsFetching(false);
        }
    };

    const handleCreateSession = async () => {
        try {
            const newSession = await createSessionMutation.mutateAsync({
                title: "New Analysis",
                imported_item_id: item.id
            });
            setCurrentSessionId(newSession.id);
            setMessages([]);
            toast({ title: 'New chat thread created', status: 'success', duration: 2000 });
        } catch (err) {
            toast({ title: 'Failed to create chat thread', status: 'error' });
        }
    };

    const handleRenameSession = (id: number, currentTitle: string) => {
        setSessionToRename({ id, title: currentTitle });
        onRenameOpen();
    };

    const handleRenameConfirm = async (newTitle: string) => {
        if (!sessionToRename) return;
        try {
            await renameSessionMutation.mutateAsync({ sessionId: sessionToRename.id, title: newTitle });
            onRenameClose();
            setSessionToRename(null);
        } catch (err) {
            toast({ title: 'Failed to rename', status: 'error' });
        }
    };

    const handleDeleteSession = (id: number) => {
        setConfirmConfig({
            title: 'Delete Conversation',
            message: 'Are you sure you want to delete this conversation thread? This action cannot be undone.',
            confirmText: 'Delete',
            confirmColor: 'red',
            isLoading: deleteSessionMutation.isPending,
            onConfirm: async () => {
                try {
                    await deleteSessionMutation.mutateAsync(id);
                    if (currentSessionId === id) setCurrentSessionId(null);
                    onDetailsConfirmClose();
                } catch (err) {
                    toast({ title: 'Failed to delete', status: 'error' });
                }
            }
        });
        onDetailsConfirmOpen();
    };

    const handleGenerateSummary = async () => {
        setIsGeneratingSummary(true);
        try {
            const response = await summaryMutation.mutateAsync(item.id);
            toast({ title: 'AI Summary Generated', status: 'success' });

            setLocalSummary(response.scope_summary);
            setLocalConfidence(0.85);



            onRefresh?.();
        } catch (error) {
            toast({ title: 'Failed to generate summary', status: 'error' });
        } finally {
            setIsGeneratingSummary(false);
        }
    };

    const handleSendMessage = async (payloadOverride?: string) => {
        const textToSend = payloadOverride || chatInput;
        if (!textToSend.trim() || isStreaming) return;

        setChatInput('');
        setIsStreaming(true);

        // Add user message + empty AI placeholder
        setMessages(prev => [...prev, { role: 'user', content: textToSend }, { role: 'ai', content: '' }]);

        try {
            await aiApi.chatStream(
                textToSend,
                item.id,
                currentSessionId || undefined,
                (chunk) => {
                    // Append chunk to the last (AI) message
                    setMessages(prev => {
                        const updated = [...prev];
                        const last = updated[updated.length - 1];
                        updated[updated.length - 1] = { ...last, content: last.content + chunk };
                        return updated;
                    });
                },
            );
        } catch (error) {
            setMessages(prev => {
                const updated = [...prev];
                updated[updated.length - 1] = { role: 'ai', content: 'Sorry, I encountered an error. Please try again.' };
                return updated;
            });
            toast({ title: 'Failed to send message', status: 'error' });
        } finally {
            setIsStreaming(false);
        }
    };

    const docStatus = item.document_status || 'none';

    // Citation Helper: Detect [Page X] and make it clickable
    const renderMessageContent = (content: string) => {
        // Pre-process: convert [Page X] into markdown links
        const processed = content.replace(
            /\[Page (\d+)\]/g,
            (_match, num) => `[📄 Page ${num}](#citation-page-${num})`
        );

        return (
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                    p: ({ children }) => <Text fontSize="sm" mb={2} color="inherit">{children}</Text>,
                    li: ({ children }) => <Box as="li" fontSize="sm" ml={4} mb={1} color="inherit">{children}</Box>,
                    ul: ({ children }) => <Box as="ul" mb={4} color="inherit">{children}</Box>,
                    ol: ({ children }) => <Box as="ol" mb={4} color="inherit">{children}</Box>,
                    table: ({ children }) => (
                        <Box overflowX="auto" my={4} borderRadius="md" border="1px" borderColor="gray.300" _dark={{ borderColor: 'whiteAlpha.300' }}>
                            <Box as="table" w="full" fontSize="xs" color="inherit" sx={{ 'th, td': { p: 2, borderBottom: '1px solid', borderColor: 'gray.200', _dark: { borderColor: 'whiteAlpha.200' } } }}>
                                {children}
                            </Box>
                        </Box>
                    ),
                    a: ({ href, children }) => {
                        // Intercept citation links
                        const match = href?.match(/^#citation-page-(\d+)$/);
                        if (match) {
                            const pageNum = parseInt(match[1], 10);
                            return (
                                <Button
                                    size="xs"
                                    variant="link"
                                    colorScheme="blue"
                                    fontWeight="bold"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setPreviewPage(pageNum);
                                        setIsPreviewOpen(true);
                                    }}
                                >
                                    {children}
                                </Button>
                            );
                        }
                        // Regular links
                        return <Link href={href} color="blue.400" isExternal>{children}</Link>;
                    },
                }}
            >
                {processed}
            </ReactMarkdown>
        );
    };

    const smartChips = [
        { label: 'Check Compliance', query: 'List all compliance requirements for this bid.' },
        { label: 'Key Deadlines', query: 'What are the critical dates and deadlines mentioned?' },
        { label: 'Margin Killers', query: 'Are there any high-risk clauses or unusual requirements in this RFP?' },
        { label: 'Bond Requirements', query: 'What are the bonding and insurance requirements?' }
    ];

    return (
        <>
            <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: '4xl' }} scrollBehavior="inside">
                <ModalOverlay bg="blackAlpha.700" />
                <ModalContent
                    h={{ base: '100vh', md: '90vh' }}
                    borderRadius={{ base: '0', md: 'xl' }}
                    overflow="hidden"
                    bg="white"
                    _dark={{ bg: 'gray.800', borderColor: 'gray.700' }}
                    border="1px"
                    borderColor="gray.200"
                    boxShadow="xl"
                >
                    <ModalHeader pb={2} borderBottomWidth="1px">
                        <Flex justify="space-between" align={{ base: 'flex-start', md: 'center' }} direction={{ base: 'column', md: 'row' }} gap={2}>
                            <VStack align="start" spacing={1}>
                                <HStack wrap="wrap">
                                    <Text fontSize={{ base: 'lg', md: 'xl' }} fontWeight="black" letterSpacing="tight">
                                        {item.item_code || 'No Item Code'}
                                    </Text>
                                    <Badge colorScheme={item.opportunity_type === 'active' ? 'green' : 'blue'} borderRadius="full" px={3}>
                                        {item.opportunity_type}
                                    </Badge>
                                </HStack>
                                <Text fontSize="sm" color="gray.500" fontWeight="medium">
                                    {item.agency} • {item.feed_name}
                                </Text>
                            </VStack>
                            <HStack spacing={4} align="center">
                                {item.unit_price > 0 && (
                                    <VStack align={{ base: 'start', md: 'end' }} spacing={0}>
                                        <Text fontSize="2xs" color="gray.500" fontWeight="bold" textTransform="uppercase">Unit Price</Text>
                                        <Text fontWeight="black" color="green.500" fontSize={{ base: 'md', md: 'lg' }}>
                                            {item.unit_price.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                                        </Text>
                                    </VStack>
                                )}
                            </HStack>
                        </Flex>
                    </ModalHeader>
                    <ModalCloseButton borderRadius="full" m={2} />

                    <ModalBody p={0} display="flex" flexDirection="row">
                        <Tabs isLazy colorScheme="brand" display="flex" flexDirection="column" h="full" w="full" size={{ base: 'sm', md: 'md' }}>
                            <TabList
                                px={{ base: 2, md: 6 }}
                                borderBottomWidth="1px"
                                pt={2}
                                bg="gray.50"
                                _dark={{ bg: 'whiteAlpha.50' }}
                                overflowX="auto"
                                overflowY="hidden"
                                sx={{
                                    scrollbarWidth: 'none',
                                    '&::-webkit-scrollbar': { display: 'none' },
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                <Tab fontSize="sm" fontWeight="bold">
                                    <HStack spacing={2}><Icon as={MdInfo} /><Text>Overview</Text></HStack>
                                </Tab>
                                <Tab fontSize="sm" fontWeight="bold">
                                    <HStack spacing={2}><Icon as={MdChat} /><Text>AI Analysis</Text></HStack>
                                </Tab>
                            </TabList>

                            <TabPanels flex="1" overflowY="auto">
                                {/* Tab 1: Overview */}
                                <TabPanel p={6}>
                                    <VStack align="stretch" spacing={6}>
                                        <HStack spacing={3} wrap="wrap">
                                            {docStatus === 'ready' && item.documents_url && (
                                                <Button size="sm" flex={{ base: 1, md: 'none' }} colorScheme="blue" leftIcon={<MdPictureAsPdf />} onClick={() => setIsPreviewOpen(true)} borderRadius="xl">View Document</Button>
                                            )}
                                            {item.source_url && (
                                                <Link href={item.source_url} isExternal>
                                                    <Button size="sm" variant="outline" leftIcon={<MdOpenInNew />} borderRadius="xl">
                                                        Source Portal
                                                    </Button>
                                                </Link>
                                            )}
                                            {(docStatus === 'none' || docStatus === 'failed') && (
                                                <Button size="sm" flex={{ base: 1, md: 'none' }} colorScheme={docStatus === 'failed' ? 'red' : 'brand'} variant="solid" leftIcon={docStatus === 'failed' ? <MdRefresh /> : <MdPictureAsPdf />} onClick={handleFetchPdf} isLoading={isFetching} borderRadius="xl">
                                                    {docStatus === 'failed' ? 'Retry Fetch' : 'Fetch PDF'}
                                                </Button>
                                            )}
                                        </HStack>

                                        <Divider />

                                        <Flex direction={{ base: 'column', md: 'row' }} gap={4}>
                                            <VStack align="start" spacing={4} flex="1">
                                                <Text fontWeight="black" fontSize="xs" color="brand.500" textTransform="uppercase" letterSpacing="wider">Logistics & Timing</Text>
                                                <Flex direction={{ base: 'column', sm: 'row', md: 'column' }} gap={3} w="full">
                                                    <Box p={3} bg="gray.50" _dark={{ bg: 'whiteAlpha.50' }} borderRadius="xl" flex={1}>
                                                        <HStack mb={1}><Icon as={MdDateRange} color="brand.400" /><Text fontSize="xs" fontWeight="bold" color="gray.500">Submission Deadline</Text></HStack>
                                                        <Text fontSize="sm" fontWeight="bold">{item.submission_deadline ? new Date(item.submission_deadline).toLocaleDateString(undefined, { dateStyle: 'medium' }) : 'N/A'}</Text>
                                                    </Box>
                                                    {item.pre_bid_meeting && (
                                                        <Box p={3} bg="gray.50" _dark={{ bg: 'whiteAlpha.50' }} borderRadius="xl" flex={1}>
                                                            <HStack mb={1}><Icon as={MdAssignment} color="brand.400" /><Text fontSize="xs" fontWeight="bold" color="gray.500">Pre-Bid Meeting</Text></HStack>
                                                            <Text fontSize="sm" fontWeight="bold">{new Date(item.pre_bid_meeting).toLocaleDateString(undefined, { dateStyle: 'medium' })}</Text>
                                                        </Box>
                                                    )}
                                                </Flex>
                                            </VStack>

                                            <VStack align="start" spacing={4} flex="1">
                                                <Text fontWeight="black" fontSize="xs" color="brand.500" textTransform="uppercase" letterSpacing="wider">Contract Details</Text>
                                                <Flex direction={{ base: 'column', sm: 'row', md: 'column' }} gap={3} w="full">
                                                    <Box p={3} bg="gray.50" _dark={{ bg: 'whiteAlpha.50' }} borderRadius="xl" flex={1}>
                                                        <HStack mb={1}><Icon as={MdPlace} color="brand.400" /><Text fontSize="xs" fontWeight="bold" color="gray.500">Location</Text></HStack>
                                                        <Text fontSize="sm" fontWeight="bold">{item.location || 'Distributed / Unknown'}</Text>
                                                    </Box>
                                                    <Box p={3} bg="gray.50" _dark={{ bg: 'whiteAlpha.50' }} borderRadius="xl" flex={1}>
                                                        <HStack mb={1}><Icon as={MdGavel} color="brand.400" /><Text fontSize="xs" fontWeight="bold" color="gray.500">Mode</Text></HStack>
                                                        <Text fontSize="sm" fontWeight="bold">{item.uom ? `By ${item.uom}` : 'Direct Award'}</Text>
                                                    </Box>
                                                </Flex>
                                            </VStack>
                                        </Flex>

                                        {/* AI Analysis Summary Card */}
                                        <Box
                                            bg="gray.50"
                                            _dark={{ bg: 'whiteAlpha.50', borderColor: 'brand.900' }}
                                            p={6}
                                            borderRadius="xl"
                                            border="1px"
                                            borderColor="brand.100"
                                        >
                                            <HStack mb={4} justify="space-between">
                                                <HStack spacing={2}>
                                                    <Icon as={MdSummarize} color="brand.500" />
                                                    <Text fontWeight="black" textTransform="uppercase" fontSize="xs" letterSpacing="widest" color="brand.600" _dark={{ color: 'brand.300' }}>AI Summary</Text>
                                                </HStack>
                                                {!displaySummary && (
                                                    <Button size="sm" colorScheme="brand" leftIcon={<MdRefresh />} isLoading={isGeneratingSummary} onClick={handleGenerateSummary} borderRadius="full">Generate AI Summary</Button>
                                                )}
                                            </HStack>
                                            {displaySummary ? (
                                                <Box>
                                                    <Text fontSize="md" color="gray.800" _dark={{ color: 'gray.100' }} lineHeight="tall">{displaySummary}</Text>
                                                    <HStack mt={4}>
                                                        <Badge colorScheme="brand" px={3} py={1} borderRadius="full">
                                                            Confidence: {Math.round(displayConfidence * 100)}%
                                                        </Badge>
                                                    </HStack>
                                                </Box>
                                            ) : (
                                                <Text fontSize="sm" color="gray.500" fontStyle="italic">Requesting AI intelligence to analyze this opportunity...</Text>
                                            )}
                                        </Box>

                                        <Box>
                                            <Text fontWeight="black" fontSize="xs" color="gray.400" textTransform="uppercase" letterSpacing="widest" mb={3}>Full Opportunity Description</Text>
                                            <Box p={4} borderRadius="xl" bg="gray.50" _dark={{ bg: 'whiteAlpha.100' }}>
                                                <Text whiteSpace="pre-wrap" fontSize="sm" lineHeight="relaxed">{item.description}</Text>
                                            </Box>
                                        </Box>
                                    </VStack>
                                </TabPanel>

                                {/* Tab 2: Chat */}
                                <TabPanel h="full" display="flex" flexDirection="column" p={0}>
                                    <Flex h="full" direction="column">
                                        {/* Mobile Session Header */}
                                        <Flex
                                            display={{ base: 'flex', md: 'none' }}
                                            p={2}
                                            bg="gray.50"
                                            _dark={{ bg: 'whiteAlpha.50' }}
                                            borderBottomWidth="1px"
                                            justify="space-between"
                                            align="center"
                                        >
                                            <IconButton
                                                size="sm"
                                                icon={<MdChat />}
                                                onClick={onSessionsOpen}
                                                aria-label="Toggle threads"
                                                variant="ghost"
                                                colorScheme="brand"
                                            />
                                            <Text fontSize="xs" fontWeight="bold">
                                                {sessions.find((s: any) => s.id === currentSessionId)?.title || 'Analysis'}
                                            </Text>
                                            <Button size="xs" colorScheme="brand" variant="outline" onClick={handleCreateSession} leftIcon={<MdAdd />}>
                                                New
                                            </Button>
                                        </Flex>

                                        <Flex flex={1} overflow="hidden">
                                            {/* Desktop Session Sidebar */}
                                            <Box
                                                w="200px"
                                                bg="gray.50"
                                                _dark={{ bg: 'rgba(0,0,0,0.2)' }}
                                                borderRightWidth="1px"
                                                p={3}
                                                display={{ base: 'none', md: 'block' }}
                                                overflowY="auto"
                                            >
                                                <SessionList />
                                            </Box>

                                            {/* Mobile Sessions Drawer */}
                                            <Drawer
                                                isOpen={isSessionsOpen}
                                                placement="left"
                                                onClose={onSessionsClose}
                                            >
                                                <DrawerOverlay />
                                                <DrawerContent bg="white" _dark={{ bg: 'gray.800' }} p={4}>
                                                    <Text fontWeight="bold" mb={4}>Analysis Threads</Text>
                                                    <SessionList />
                                                </DrawerContent>
                                            </Drawer>

                                            {/* Chat Area */}
                                            <Flex flex={1} direction="column" bg="white" _dark={{ bg: 'gray.900' }}>
                                                <VStack ref={chatScrollRef} flex="1" align="stretch" spacing={4} overflowY="auto" px={6} py={6}>
                                                    {messages.length === 0 ? (
                                                        <VStack flex="1" justify="center" align="center" spacing={4}>
                                                            <Icon as={MdStars} boxSize={12} color="brand.200" />
                                                            <Text fontWeight="bold" fontSize="lg" color="gray.600" _dark={{ color: 'gray.400' }}>AI Assistant</Text>
                                                            <Text textAlign="center" fontSize="sm" color="gray.500" maxW="300px">Ask anything about the scope, compliance, or risks in this RFP.</Text>
                                                        </VStack>
                                                    ) : (
                                                        messages.map((msg, idx) => (
                                                            <Box
                                                                key={idx}
                                                                alignSelf={msg.role === 'user' ? 'flex-end' : 'flex-start'}
                                                                bg={msg.role === 'user' ? 'brand.600' : 'gray.50'}
                                                                color={msg.role === 'user' ? 'white' : 'gray.900'}
                                                                px={4}
                                                                py={3}
                                                                borderRadius="xl"
                                                                borderTopRightRadius={msg.role === 'user' ? '2px' : 'xl'}
                                                                borderTopLeftRadius={msg.role === 'user' ? 'xl' : '2px'}
                                                                boxShadow="sm"
                                                                maxW="85%"
                                                                border="1px"
                                                                borderColor={msg.role === 'user' ? 'brand.700' : 'gray.200'}
                                                                _dark={{
                                                                    bg: msg.role === 'user' ? 'brand.500' : 'gray.800',
                                                                    color: 'white',
                                                                    borderColor: msg.role === 'user' ? 'brand.400' : 'gray.700'
                                                                }}
                                                            >
                                                                {renderMessageContent(msg.content)}
                                                            </Box>
                                                        ))
                                                    )}
                                                    {isStreaming && messages[messages.length - 1]?.content === '' && (
                                                        <Box alignSelf="flex-start" bg="gray.50" _dark={{ bg: 'gray.800', borderColor: 'gray.700' }} px={4} py={3} borderRadius="2xl" borderTopLeftRadius="4px" border="1px" borderColor="gray.200">
                                                            <HStack spacing={1}>
                                                                <Box w={2} h={2} bg="brand.400" borderRadius="full" className="bounce-1" />
                                                                <Box w={2} h={2} bg="brand.400" borderRadius="full" className="bounce-2" />
                                                                <Box w={2} h={2} bg="brand.400" borderRadius="full" className="bounce-3" />
                                                            </HStack>
                                                        </Box>
                                                    )}
                                                </VStack>

                                                <Box p={4} borderTopWidth="1px" bg="gray.50" _dark={{ bg: 'whiteAlpha.50' }}>
                                                    <HStack spacing={2} mb={3} wrap="wrap">
                                                        {smartChips.map((chip, idx) => (
                                                            <Tag key={idx} size="md" variant="subtle" colorScheme="brand" borderRadius="full" cursor="pointer" onClick={() => handleSendMessage(chip.query)} _hover={{ bg: 'brand.100' }}>
                                                                <TagLabel fontSize="xs">{chip.label}</TagLabel>
                                                            </Tag>
                                                        ))}
                                                    </HStack>
                                                    <InputGroup size="md">
                                                        <Input
                                                            pr="4.5rem"
                                                            placeholder="Ask your AI agent..."
                                                            value={chatInput}
                                                            onChange={(e) => setChatInput(e.target.value)}
                                                            onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                                                            bg="white"
                                                            _dark={{ bg: 'gray.800' }}
                                                            borderRadius="lg"
                                                            fontSize="sm"
                                                            _focus={{ borderColor: 'brand.500', boxShadow: 'none' }}
                                                        />
                                                        <InputRightElement width="4.5rem">
                                                            <Button
                                                                h="1.75rem"
                                                                size="sm"
                                                                colorScheme="brand"
                                                                isDisabled={!chatInput || isStreaming}
                                                                isLoading={isStreaming}
                                                                onClick={() => handleSendMessage()}
                                                            >
                                                                Send
                                                            </Button>
                                                        </InputRightElement>
                                                    </InputGroup>
                                                </Box>
                                            </Flex>
                                        </Flex>
                                    </Flex>
                                </TabPanel>
                            </TabPanels>
                        </Tabs>
                    </ModalBody>

                    <ModalFooter px={6} py={4} borderTopWidth="1px" bg="gray.50" _dark={{ bg: 'gray.800' }}>
                        <Button variant="ghost" mr={3} onClick={onClose} borderRadius="lg">Cancel</Button>
                        {item.status !== 'moved' && (
                            <Button
                                colorScheme="brand"
                                size="md"
                                isLoading={isMoving}
                                onClick={() => onMove(item)}
                                borderRadius="lg"
                                px={8}
                            >
                                MOVE TO PURSUITS
                            </Button>
                        )}
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <PDFPreviewModal
                isOpen={isPreviewOpen}
                onClose={() => { setIsPreviewOpen(false); setPreviewPage(undefined); }}
                pdfUrl={`${import.meta.env.VITE_API_URL || '/api'}/v1/documents/bid/${item.id}/pdf`}
                title={`${item.item_code} - Intelligence Review`}
                page={previewPage}
            />

            {isDetailsConfirmOpen && confirmConfig && (
                <ConfirmDialog
                    isOpen={isDetailsConfirmOpen}
                    onClose={onDetailsConfirmClose}
                    onConfirm={confirmConfig.onConfirm}
                    title={confirmConfig.title}
                    message={confirmConfig.message}
                    confirmText={confirmConfig.confirmText}
                    confirmColor={confirmConfig.confirmColor}
                    isLoading={confirmConfig.isLoading}
                />
            )}

            <RenameDialog
                isOpen={isRenameOpen}
                onClose={onRenameClose}
                onRename={handleRenameConfirm}
                initialTitle={sessionToRename?.title || ''}
                isLoading={renameSessionMutation.isPending}
            />
            <style>{`
                .bounce-1 { animation: bounce 0.6s infinite ease-in-out; }
                .bounce-2 { animation: bounce 0.6s infinite 0.2s ease-in-out; }
                .bounce-3 { animation: bounce 0.6s infinite 0.4s ease-in-out; }
                @keyframes bounce {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-4px); }
                }
            `}</style>
        </>
    );
}
